import { useEffect } from "react";
import { Navigate, useNavigate, useSearchParams, Link } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { toast } from "sonner";
import { LogIn } from "lucide-react";
import { Input } from "../components/ui/Input.jsx";
import { Button } from "../components/ui/Button.jsx";
import * as authApi from "../api/auth.api.js";
import { useAuthStore, selectIsAuthed } from "../store/auth.store.js";

const schema = z.object({
  email: z.string().email("Invalid email address"),
  password: z.string().min(1, "Password is required"),
});

export default function LoginPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const next = searchParams.get("next") ?? "/";
  const authed = useAuthStore(selectIsAuthed);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    setError,
  } = useForm({ resolver: zodResolver(schema) });

  useEffect(() => {
    document.title = "Sign in · Shere-e-Punjab Admin";
  }, []);

  if (authed) return <Navigate to={next} replace />;

  const onSubmit = async (data) => {
    try {
      const result = await authApi.login(data);
      useAuthStore.getState().setSession(result.user, result.accessToken);
      toast.success(`Welcome back, ${result.user.name.split(" ")[0]}`);
      navigate(next, { replace: true });
    } catch (err) {
      const message = err?.response?.data?.message ?? "Login failed";
      setError("password", { message });
    }
  };

  return (
    <main className="flex min-h-screen items-center justify-center px-6 py-12">
      <div className="w-full max-w-md">
        <div className="mb-8 text-center">
          <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-xl bg-accent text-bg-base">
            <LogIn className="h-5 w-5" />
          </div>
          <h1 className="font-display text-3xl text-text-primary">Admin Sign In</h1>
          <p className="mt-2 text-sm text-text-secondary">
            Manage Shere-e-Punjab Homes content & submissions.
          </p>
        </div>

        <form
          onSubmit={handleSubmit(onSubmit)}
          className="card space-y-5 rounded-xl border border-border-subtle bg-bg-elevated p-6 shadow-card"
          noValidate
        >
          <Input
            label="Email"
            type="email"
            autoComplete="email"
            autoFocus
            required
            error={errors.email?.message}
            {...register("email")}
          />
          <Input
            label="Password"
            type="password"
            autoComplete="current-password"
            required
            error={errors.password?.message}
            {...register("password")}
          />

          <Button type="submit" className="w-full" loading={isSubmitting}>
            {isSubmitting ? "Signing in…" : "Sign in"}
          </Button>

          <div className="text-center text-xs">
            <Link to="/forgot-password" className="text-text-secondary hover:text-accent">
              Forgot your password?
            </Link>
          </div>
        </form>

        <p className="mt-6 text-center text-xs text-text-muted">
          Trouble signing in? Contact your super admin.
        </p>
      </div>
    </main>
  );
}
